import React, { useEffect, useState } from "react";
import { MdPerson, MdEmail, MdPhone, MdEvent, MdCheckCircle } from "react-icons/md";
import Modal from "./Modal";

const defaultExcelFields = [
  { name: "Hall" },
  { name: "Stand Number" },
  { name: "Exhibitor Name" },
  { name: "Size" },
  { name: "Tent Number" },
];

const AddUserModal = ({
  isOpen,
  onClose,
  onSubmit,
  form,
  setForm,
  isSubmitting = false,
}) => {
  const [step, setStep] = useState(1);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(e);
  };

  const steps = [
    { number: 1, title: "Event & User Info" },
    { number: 2, title: "Excel Template" },
    { number: 3, title: "Activities" },
    { number: 4, title: "Event Dates & Logistics" }
  ];

  const safeDates = form?.dates || {
    buildUp: { start: "", end: "" },
    showDays: { start: "", end: "" },
    teardown: { start: "", end: "" },
  };

  useEffect(() => {
    if (step === 2 && (!form.customFields || form.customFields.length === 0)) {
      setForm({
        ...form,
        customFields: defaultExcelFields,
      });
    }
  }, [step]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create User & Event" size="lg">
      <div className="flex flex-col h-full">

        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex justify-between items-center">
            {steps.map((s, idx) => (
              <React.Fragment key={s.number}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${step > s.number ? 'bg-green-500 text-white' :
                    step === s.number ? 'bg-blue-600 text-white' :
                      'bg-gray-300 text-gray-600'
                    }`}>
                    {step > s.number ? <MdCheckCircle className="text-xl" /> : s.number}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${step === s.number ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                    {s.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`h-1 flex-1 mx-2 rounded transition-all ${step > s.number ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="px-6 py-6 space-y-5 flex-1 overflow-y-auto">

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Event Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MdEvent className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  <input
                    value={form.eventName || ""}
                    onChange={(e) => setForm({ ...form, eventName: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    placeholder="Enter event name"
                    required
                  />
                </div>
              </div>

              <div className="h-px bg-gray-200 my-4"></div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MdPerson className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  <input
                    value={form.name || ""}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    placeholder="Enter full name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MdEmail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  <input
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    placeholder="Enter email address"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <MdPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  <input
                    value={form.phone || ""}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Default Excel Template Fields
                </label>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Field Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {defaultExcelFields.map((field, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-4 py-2 text-gray-700">{field.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  These default fields will be automatically included and won't be saved separately.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Custom Fields <span className="text-gray-400 text-xs">(Optional)</span>
                </label>

                {(form.customFields || []).filter((_, i) => i >= defaultExcelFields.length).map((field, index) => {
                  const actualIndex = index + defaultExcelFields.length;
                  return (
                    <div
                      key={actualIndex}
                      className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-3"
                    >
                      <input
                        placeholder="Field label (e.g. Company, Role, Badge Type)"
                        value={field.name}
                        onChange={(e) => {
                          const updated = [...form.customFields];
                          updated[actualIndex].name = e.target.value;
                          setForm({ ...form, customFields: updated });
                        }}
                        className="flex-1 bg-transparent outline-none text-gray-700 font-medium placeholder-gray-400"
                      />

                      <button
                        type="button"
                        onClick={() => {
                          const updated = form.customFields.filter((_, i) => i !== actualIndex);
                          setForm({ ...form, customFields: updated });
                        }}
                        className="text-red-500 hover:text-red-700 font-bold text-xl leading-none"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      customFields: [...(form.customFields || []), { name: "" }],
                    })
                  }
                  className="w-full py-2.5 border-2 border-dashed border-blue-400 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                >
                  + Add Custom Field
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">

              <div className="space-y-3">
                {form.activities.map((activity, index) => (
                  <div
                    key={activity.key || index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >

                    <input
                      value={activity.name}
                      onChange={(e) => {
                        const updated = [...form.activities];
                        updated[index].name = e.target.value;
                        setForm({ ...form, activities: updated });
                      }}
                      className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg"
                      placeholder="Activity name"
                    />

                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => {
                        const updated = form.activities.filter((_, i) => i !== index);
                        setForm({ ...form, activities: updated });
                      }}
                      className="text-red-500 hover:text-red-700 text-xl font-bold"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              {/* Add Activity */}
              <button
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    activities: [
                      ...form.activities,
                      { key: null, name: "", percent: 0 },
                    ],
                  })
                }
                className="w-full py-2.5 border-2 border-dashed border-blue-400 text-blue-600 rounded-lg hover:bg-blue-50 font-medium"
              >
                + Add Activity
              </button>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span>Build Up</span>
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={safeDates.buildUp.start}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          dates: {
                            ...safeDates,
                            buildUp: { ...safeDates.buildUp, start: e.target.value },
                          },
                        })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={safeDates.buildUp.end}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          dates: {
                            ...safeDates,
                            buildUp: { ...safeDates.buildUp, end: e.target.value },
                          },
                        })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span>Show Days</span>
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={safeDates.showDays.start}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          dates: {
                            ...safeDates,
                            showDays: { ...safeDates.showDays, start: e.target.value },
                          },
                        })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={safeDates.showDays.end}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          dates: {
                            ...safeDates,
                            showDays: { ...safeDates.showDays, end: e.target.value },
                          },
                        })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span>Teardown</span>
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={safeDates.teardown.start}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          dates: {
                            ...safeDates,
                            teardown: { ...safeDates.teardown, start: e.target.value },
                          },
                        })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={safeDates.teardown.end}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          dates: {
                            ...safeDates,
                            teardown: { ...safeDates.teardown, end: e.target.value },
                          },
                        })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                </div>
              </div>
              {/* Logistics Section */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-5">
                <h4 className="font-semibold text-gray-800 text-lg">
                  Event Logistics
                </h4>

                {/* Numbers */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Number of Halls
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.hallsNumber || ""}
                      onChange={(e) =>
                        setForm({ ...form, hallsNumber: Number(e.target.value) })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="e.g. 10"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Number of Tents
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.tentsNumber || ""}
                      onChange={(e) =>
                        setForm({ ...form, tentsNumber: Number(e.target.value) })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="e.g. 10"
                    />
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-semibold text-gray-800">Electricity Available</p>
                      <p className="text-xs text-gray-500">Power supply for stands & halls</p>
                    </div>

                    <input
                      type="checkbox"
                      checked={form.electricity ?? true}
                      onChange={(e) =>
                        setForm({ ...form, electricity: e.target.checked })
                      }
                      className="w-5 h-5 accent-blue-600"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-semibold text-gray-800">Internet Available</p>
                      <p className="text-xs text-gray-500">WiFi or wired connection</p>
                    </div>

                    <input
                      type="checkbox"
                      checked={form.internet ?? true}
                      onChange={(e) =>
                        setForm({ ...form, internet: e.target.checked })
                      }
                      className="w-5 h-5 accent-blue-600"
                    />
                  </div>
                </div>
              </div>

            </div>
          )}

        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <button
            type="button"
            onClick={step > 1 ? () => setStep(step - 1) : onClose}
            className="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            {step > 1 ? "← Back" : "Cancel"}
          </button>

          {step < 4 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md"
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              className={`px-6 py-2.5 rounded-lg font-medium shadow-md transition-all bg-green-600 text-white hover:bg-green-700}`}
            >
              {isSubmitting ? "Creating..." : "✓ Create"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddUserModal;